import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { formatPhoneNumber } from '@/utils/phoneFormat';
import { z } from 'zod';

const privateTrustSchema = z.object({
  formType: z.string().default('Trust Formation / Estate Planning'),
  firstName: z.string().trim().min(1, { message: "First name is required" }).max(50, { message: "First name must be less than 50 characters" }),
  lastName: z.string().trim().min(1, { message: "Last name is required" }).max(50, { message: "Last name must be less than 50 characters" }),
  email: z.string().trim().email({ message: "Invalid email address" }).max(255, { message: "Email must be less than 255 characters" }),
  phone: z.string().trim().min(1, { message: "Phone number is required" }).max(20, { message: "Phone number must be less than 20 characters" }),
  countryRegion: z.string().trim().min(1, { message: "Country or region is required" }).max(100, { message: "Country or region must be less than 100 characters" }),
  timeline: z.string().trim().min(1, { message: "Please select a timeline" })
});

type PrivateTrustFormData = z.infer<typeof privateTrustSchema>;

interface PrivateTrustFormProps {
  onSuccess?: () => void;
}

export function PrivateTrustForm({ onSuccess }: PrivateTrustFormProps = {}) {
  const { toast } = useToast();
  const [formData, setFormData] = useState<PrivateTrustFormData>({
    formType: 'Trust Formation / Estate Planning',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryRegion: '',
    timeline: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof PrivateTrustFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);


  const timelineOptions = [
    { value: 'asap', label: 'As soon as possible' },
    { value: '1-2weeks', label: '1-2 weeks' },
    { value: '3-4weeks', label: '3-4 weeks' },
    { value: '1-2months', label: '1-2 months' },
    { value: '3-6months', label: '3-6 months' },
    { value: 'flexible', label: 'Flexible timeline' }
  ];

  const handleInputChange = (field: keyof PrivateTrustFormData, value: string) => {
    // Format phone number as user types
    if (field === 'phone') {
      value = formatPhoneNumber(value);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    // Close any open dropdowns
    document.body.click();

    try {
      const validatedData = privateTrustSchema.parse(formData);
      
      // Send data to Zapier webhook
      const response = await fetch('https://hooks.zapier.com/hooks/catch/21776725/u1pdpuq/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify({
          ...validatedData,
          timestamp: new Date().toISOString(),
          source: window.location.origin
        }),
      });

      // Close the form first if onSuccess callback is provided
      if (onSuccess) {
        onSuccess();
      }

      // Small delay to let the form close before showing notification
      setTimeout(() => {
        toast({
          title: "Request Submitted Successfully",
          description: "Thank you for your interest in private trust establishment. We'll be in touch with you soon to discuss next steps.",
        });
      }, 300);

      // Reset form after successful submission
      handleReset();
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof PrivateTrustFormData, string>> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            fieldErrors[err.path[0] as keyof PrivateTrustFormData] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          title: "Error",
          description: "Failed to submit your request. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      formType: 'Trust Formation / Estate Planning',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryRegion: '',
      timeline: ''
    });
    setErrors({});
  };

  return (
    <div className="max-w-2xl -mx-2 md:mx-auto">
      <Card className="bg-gradient-to-r from-primary/5 to-gold/5 border-gold/20">
        <CardHeader>
          <CardTitle className="font-rockwell text-2xl text-primary text-center">
            Get Started with Private Trust Establishment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={errors.firstName ? 'border-red-500' : ''}
                />
                {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={errors.lastName ? 'border-red-500' : ''}
                />
                {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={errors.phone ? 'border-red-500' : ''}
              />
              {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="countryRegion">Country or Region for Trust Establishment</Label>
              <Input
                id="countryRegion"
                value={formData.countryRegion}
                onChange={(e) => handleInputChange('countryRegion', e.target.value)}
                placeholder="Enter country or region"
                className={errors.countryRegion ? 'border-red-500' : ''}
              />
              {errors.countryRegion && <p className="text-sm text-red-500">{errors.countryRegion}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="timeline">Timeline for Trust Setup</Label>
              <Select value={formData.timeline} onValueChange={(value) => handleInputChange('timeline', value)}>
                <SelectTrigger className={`focus:ring-gold focus:border-gold ${errors.timeline ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="How soon do you need this?" />
                </SelectTrigger>
                <SelectContent>
                  {timelineOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.timeline && <p className="text-sm text-red-500">{errors.timeline}</p>}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                className="flex-1"
              >
                Reset
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}